import React, { useMemo, useState, useCallback } from "react";

import { Checkbox, Stack, Typography } from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import IndeterminateCheckBoxIcon from "@mui/icons-material/IndeterminateCheckBox";

import { WorkspaceFolderOpenIcon, WorkspaceFolderCloseIcon } from "assets/material-custom-icons";
import { UnitedArray, UnitedList } from "./UnitedList";
import { useFilesPickerContext } from "../hooks/files-picker-context";
import { FileType } from "../../../../constants";
import { IFile, IFolder } from "store/types";

interface IProps {
  folder: IFolder;
  subIndex: number;
}

export const Folder: React.FC<IProps> = ({ folder, subIndex }) => {
  const [arrow, setArrow] = useState<boolean>(false);
  const { isItemSelected, files, selectAllInFolder, resultFolders, singleFileSelection } = useFilesPickerContext();

  const onArrowClick = () => setArrow(!arrow);

  const subFolders = useMemo(() => {
    return resultFolders.filter((item) => item.parent_id === folder.id);
  }, [resultFolders, folder.id]);

  const filesToRender = useMemo(() => {
    return files.filter((item) => item.section_id === folder.id && item.type === FileType.PDF && item.form_id === null);
  }, [files, folder.id]);

  const allSubfolderFiles = useMemo(() => {
    const getAllSubfolderFiles = (folderId: number): IFile[] => {
      const currentFolderFiles = files.filter(
        (item) => item.section_id === folderId && item.type === FileType.PDF && item.form_id === null
      );
      const subFolders = resultFolders.filter((folder) => folder.parent_id === folderId);
      const subFolderFiles = subFolders.flatMap((subfolder) => getAllSubfolderFiles(subfolder.id));
      return [...currentFolderFiles, ...subFolderFiles];
    };

    return getAllSubfolderFiles(folder.id);
  }, [files, folder.id, resultFolders]);

  const handleFolderSelect = useCallback(() => {
    selectAllInFolder(allSubfolderFiles);
  }, [folder.id, allSubfolderFiles, selectAllInFolder]);

  // Some entities have "title" others "name"
  const wholeData: UnitedArray = useMemo(() => {
    return [...filesToRender].sort((a, b) => {
      const aParam = (a as IFile).name || "";
      const bParam = (b as IFile).name || "";
      return aParam.localeCompare(bParam);
    });
  }, [filesToRender]);

  const hasSelectableFiles = useMemo(() => {
    return allSubfolderFiles.length > 0;
  }, [allSubfolderFiles]);

  const isFolderIndeterminate = useMemo(() => {
    if (!hasSelectableFiles) return false;
    const selectedCount = allSubfolderFiles.filter((file) => isItemSelected(file.id, "files")).length;
    return selectedCount > 0 && selectedCount < allSubfolderFiles.length;
  }, [allSubfolderFiles, isItemSelected, hasSelectableFiles]);

  const isFolderSelected = useMemo(() => {
    if (!hasSelectableFiles) return false;
    return allSubfolderFiles.every((file) => isItemSelected(file.id, "files"));
  }, [allSubfolderFiles, isItemSelected, hasSelectableFiles]);

  return (
    <>
      <Stack direction="row" alignItems="center">
        <Checkbox
          checkedIcon={<CheckBoxIcon sx={{ fill: isFolderSelected ? "#0A3142" : "gray" }} />}
          checked={isFolderSelected}
          onChange={handleFolderSelect}
          indeterminate={isFolderIndeterminate}
          indeterminateIcon={<IndeterminateCheckBoxIcon sx={{ fill: "#0A3142" }} />}
          disabled={!hasSelectableFiles || singleFileSelection}
        />
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            paddingLeft: subIndex == 1 ? "10px" : `${(subIndex - 1) * 40}px`,
            width: "90%",
            height: "100%",
            borderBottom: "1px solid  #DAE0E2"
          }}
          gap="10px"
        >
          {arrow ? (
            <KeyboardArrowDownIcon sx={{ cursor: "pointer" }} onClick={onArrowClick} />
          ) : (
            <KeyboardArrowRightIcon sx={{ cursor: "pointer" }} onClick={onArrowClick} />
          )}
          {arrow ? (
            <WorkspaceFolderOpenIcon sx={{ fill: "#0A3142" }} />
          ) : (
            <WorkspaceFolderCloseIcon sx={{ fill: "#0A3142" }} />
          )}
          <Typography
            sx={{
              fontSize: "16px",
              color: "#0a3142",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              overflow: "hidden"
            }}
          >
            {folder.title}
          </Typography>
        </Stack>
      </Stack>
      {arrow && <UnitedList data={wholeData} subIndex={subIndex + 1} />}
      {arrow && subFolders.map((subFolder) => <Folder key={subFolder.id} folder={subFolder} subIndex={subIndex + 1} />)}
    </>
  );
};
